/**
 * Public tournament subpages that get a request-time shell.
 * Cold HTML for these paths must carry that path's title and canonical.
 * Director routes stay on the neutral app shell and are not listed here.
 */

const CANONICAL_ORIGIN = "https://www.bowlingtournamentapp.com";

const PUBLIC_PAGES = {
  standings: {
    sitemap: true,
    label: "standings",
    heading: "Standings",
    description: (name) => `Standings for ${name}.`,
  },
  squads: {
    sitemap: true,
    label: "squads",
    heading: "Squads",
    description: (name) => `Squads for ${name}.`,
  },
  prizes: {
    sitemap: true,
    label: "prizes",
    heading: "Prizes",
    description: (name) => `Prizes for ${name}.`,
  },
  payout: {
    sitemap: true,
    label: "payouts",
    heading: "Payouts",
    description: (name) => `Payouts for ${name}.`,
  },
  scoring: {
    sitemap: true,
    label: "scoring",
    heading: "Scoring",
    description: (name) => `Scoring for ${name}.`,
  },
  "scoring/scores": {
    sitemap: false,
    label: "scoring",
    heading: "Scoring",
    description: (name) => `Scoring for ${name}.`,
  },
  "scoring/lanes": {
    sitemap: false,
    label: "lanes",
    heading: "Lanes",
    description: (name) => `Lane assignments for ${name}.`,
  },
  rules: {
    sitemap: true,
    label: "rules",
    heading: "Event rules",
    description: (name) => `Event rules for ${name}.`,
  },
  averages: {
    sitemap: true,
    label: "averages",
    heading: "Averages",
    description: (name) => `Bowler averages for ${name}.`,
  },
  contacts: {
    sitemap: true,
    label: "contacts",
    heading: "Contacts",
    description: (name) => `Contacts for ${name}.`,
  },
  signups: {
    sitemap: true,
    label: "signups",
    heading: "Signups",
    description: (name) => `Signups for ${name}.`,
  },
};

const ALIASES = {
  results: "standings",
  payouts: "payout",
  teams: "scoring",
};

const SPA_EXACT = new Set([
  "admin",
  "setup",
  "settings",
  "finance",
  "brackets",
  "signup-dashboard",
  "qr-codes",
  "signups/cart",
]);

const SITEMAP_SUBPAGE_PATHS = Object.entries(PUBLIC_PAGES)
  .filter(([, page]) => page.sitemap)
  .map(([suffix]) => suffix);

const flattenPart = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value.flatMap(flattenPart);
  return String(value).split("/");
};

const normalizeSuffix = (value) => {
  const joined = flattenPart(value).join("/");
  let decoded = joined;
  try {
    decoded = decodeURIComponent(joined);
  } catch {
    decoded = joined;
  }
  return decoded
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .join("/");
};

const suffixFromQuery = (query = {}) => {
  if (query.shellPath != null && String(query.shellPath) !== "") {
    return normalizeSuffix(query.shellPath);
  }
  if (query.path != null && String(query.path) !== "") {
    return normalizeSuffix(query.path);
  }
  return normalizeSuffix(
    ["p1", "a", "p2", "b", "p3", "c", "p4", "d"].map((key) => query[key])
  );
};

const isKnownTournamentSpa = (suffix) => {
  if (SPA_EXACT.has(suffix)) return true;
  if (/^finance\/[^/]+$/.test(suffix)) return true;
  if (/^signup-dashboard\/[^/]+$/.test(suffix)) return true;
  if (/^signup-edit\/[^/]+$/.test(suffix)) return true;
  if (/^signups\/edit\/[^/]+$/.test(suffix)) return true;
  if (/^teams\/[^/]+$/.test(suffix)) return true;
  if (/^payout-dashboard(?:\/.*)?$/.test(suffix)) return true;
  return false;
};

const resolveTournamentSuffix = (suffix) => {
  const normalized = normalizeSuffix(suffix);
  if (!normalized) return { kind: "overview", suffix: "" };
  if (normalized.split("/").some((part) => part === "." || part === "..")) {
    return { kind: "notFound", suffix: normalized };
  }
  if (ALIASES[normalized]) {
    return { kind: "redirect", suffix: normalized, target: ALIASES[normalized] };
  }
  if (/^scoring\/[^/]+$/.test(normalized) && !PUBLIC_PAGES[normalized]) {
    return { kind: "redirect", suffix: normalized, target: "scoring/scores" };
  }
  if (PUBLIC_PAGES[normalized]) {
    return { kind: "shell", suffix: normalized };
  }
  if (isKnownTournamentSpa(normalized)) {
    return { kind: "spa", suffix: normalized };
  }
  return { kind: "notFound", suffix: normalized };
};

const subpageMeta = (suffix, name) => {
  const page = PUBLIC_PAGES[normalizeSuffix(suffix)];
  if (!page) return null;
  const tournamentName = String(name || "").trim() || "Tournament";
  return {
    title: `${tournamentName} ${page.label} - Bowling Tournament App`,
    description: page.description(tournamentName),
    heading: page.heading,
  };
};

const canonicalUrl = (path) => `${CANONICAL_ORIGIN}${path}`;

const tournamentRequestPath = (key, suffix) =>
  `/tournament/${key}${suffix ? `/${normalizeSuffix(suffix)}` : ""}`;

module.exports = {
  ALIASES,
  CANONICAL_ORIGIN,
  PUBLIC_PAGES,
  SITEMAP_SUBPAGE_PATHS,
  canonicalUrl,
  normalizeSuffix,
  resolveTournamentSuffix,
  subpageMeta,
  suffixFromQuery,
  tournamentRequestPath,
};
